import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export interface EnvironmentData {
  environments: Array<{
    id: number;
    environment_name: string;
    environment_description: string;
    is_current: boolean;
    generic_environment?: {
      name: string;
      code: string;
    };
  }>;
  current_environment_id?: number;
}

@customElement('environment-switcher-modal')
export class EnvironmentSwitcherModal extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: Object }) environmentData: EnvironmentData | null = null;
  @state() private selectedEnvironmentId: number | null = null;

  static styles = css`
    /* Video Game Style Environment Switcher Modal */
    .environment-modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      backdrop-filter: blur(12px);
      z-index: 2000; /* Tier 1: Standard modals (mutually exclusive with other modals) */
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
    }

    .environment-modal-overlay.open {
      opacity: 1;
      pointer-events: all;
    }

    .environment-modal {
      width: min(90vw, 600px);
      max-height: min(80vh, 500px);
      background: linear-gradient(145deg, #1a1a2e, #16213e);
      border-radius: 20px;
      border: 3px solid #0f3460;
      box-shadow: 
        0 0 30px rgba(15, 52, 96, 0.5),
        inset 0 1px 0 rgba(255, 255, 255, 0.1);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      transform: scale(0.8) translateY(50px);
      transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
      position: relative;
    }

    .environment-modal-overlay.open .environment-modal {
      transform: scale(1) translateY(0);
    }

    .environment-modal-header {
      background: linear-gradient(90deg, #0f3460, #16213e);
      padding: 1.2rem;
      border-bottom: 2px solid #0f3460;
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative;
    }

    .environment-modal-header::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg, #4fc3f7, #29b6f6, #03a9f4);
    }

    .environment-modal-title {
      color: #e3f2fd;
      font-size: 1.6rem;
      font-weight: 700;
      display: flex;
      align-items: center;
      gap: 0.8rem;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    }

    .environment-modal-close {
      background: linear-gradient(145deg, #e53935, #c62828);
      border: 2px solid #b71c1c;
      color: white;
      padding: 10px 16px;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.3s ease;
      font-weight: 600;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    }

    .environment-modal-close:hover {
      background: linear-gradient(145deg, #f44336, #d32f2f);
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(244, 67, 54, 0.4);
    }

    .environment-modal-close:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }

    .environment-modal-body {
      flex: 1;
      padding: 1.5rem;
      overflow-y: auto;
    }

    .environment-info {
      background: linear-gradient(135deg, #0f3460, #16213e);
      border: 2px solid #29b6f6;
      border-radius: 16px;
      padding: 1rem;
      margin-bottom: 1.5rem;
      text-align: center;
    }

    .current-environment {
      color: #4fc3f7;
      font-size: 1.1rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
    }

    .environment-subtitle {
      color: #e3f2fd;
      font-size: 0.9rem;
      opacity: 0.8;
    }

    .environments-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 1rem;
    }

    .environment-card {
      background: linear-gradient(145deg, #1e3a8a, #1e40af);
      border: 2px solid #3b82f6;
      border-radius: 16px;
      padding: 1.2rem;
      cursor: pointer;
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;
    }

    .environment-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, #60a5fa, #3b82f6, #2563eb);
    }

    .environment-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
      border-color: #60a5fa;
    }

    .environment-card.current {
      border-color: #10b981;
      background: linear-gradient(145deg, #065f46, #047857);
    }

    .environment-card.current::before {
      background: linear-gradient(90deg, #34d399, #10b981, #059669);
    }

    .environment-card.selected {
      border-color: #f59e0b;
      background: linear-gradient(145deg, #92400e, #b45309);
      transform: translateY(-4px) scale(1.02);
    }

    .environment-card.selected::before {
      background: linear-gradient(90deg, #fbbf24, #f59e0b, #d97706);
    }

    .environment-card-header {
      display: flex;
      align-items: center;
      gap: 0.8rem;
      margin-bottom: 0.8rem;
    }

    .environment-icon {
      font-size: 2rem;
    }

    .environment-name {
      color: #e3f2fd;
      font-size: 1.1rem;
      font-weight: 700;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    }

    .environment-status {
      background: linear-gradient(145deg, #10b981, #059669);
      color: white;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 0.7rem;
      font-weight: 600;
      margin-left: auto;
    }

    .environment-description {
      color: #cbd5e1;
      font-size: 0.9rem;
      line-height: 1.4;
      margin-bottom: 1rem;
    }

    .environment-type {
      color: #94a3b8;
      font-size: 0.8rem;
      font-style: italic;
    }

    .modal-actions {
      padding: 1.5rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      display: flex;
      gap: 1rem;
      justify-content: flex-end;
    }

    .action-button {
      padding: 0.8rem 1.5rem;
      border-radius: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      border: 2px solid transparent;
    }

    .action-button.cancel {
      background: linear-gradient(145deg, #6b7280, #4b5563);
      color: white;
      border-color: #374151;
    }

    .action-button.cancel:hover {
      background: linear-gradient(145deg, #4b5563, #374151);
      transform: translateY(-2px);
    }

    .action-button.confirm {
      background: linear-gradient(145deg, #10b981, #059669);
      color: white;
      border-color: #047857;
    }

    .action-button.confirm:hover {
      background: linear-gradient(145deg, #059669, #047857);
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(16, 185, 129, 0.4);
    }

    .action-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none !important;
    }

    /* Mobile Responsive */
    @media (max-width: 768px) {
      .environment-modal {
        width: 95vw;
        max-height: 85vh;
        margin: 0 2.5vw;
      }

      .environments-grid {
        grid-template-columns: 1fr;
      }

      .environment-modal-title {
        font-size: 1.3rem;
      }

      .modal-actions {
        flex-direction: column;
      }
    }
  `;

  /**
   * Get environment icon based on type or name
   */
  private getEnvironmentIcon(environment: any): string {
    const name = (environment.environment_name || '').toLowerCase();
    const code = (environment.generic_environment?.code || '').toLowerCase();

    if (name.includes('home') || code.includes('home')) return '🏠';
    if (name.includes('office') || code.includes('office')) return '🏢';
    if (name.includes('gym') || code.includes('gym')) return '💪';
    if (name.includes('park') || code.includes('park')) return '🌳';
    if (name.includes('cafe') || code.includes('cafe')) return '☕';
    if (name.includes('library') || code.includes('library')) return '📚';
    if (name.includes('outdoor') || code.includes('outdoor')) return '🌤️';
    if (name.includes('indoor') || code.includes('indoor')) return '🏠';

    return '📍';
  }

  /**
   * Handle environment selection
   */
  private selectEnvironment = (environmentId: number) => {
    this.selectedEnvironmentId = environmentId;
  };

  /**
   * Handle environment switch confirmation
   */
  private confirmSwitch = () => {
    if (this.selectedEnvironmentId) {
      this.dispatchEvent(new CustomEvent('switch-environment', {
        detail: { environmentId: this.selectedEnvironmentId },
        bubbles: true
      }));
    }
    this.closeModal();
  };

  /**
   * Close modal
   */
  private closeModal = () => {
    this.selectedEnvironmentId = null;
    this.dispatchEvent(new CustomEvent('close-modal', { bubbles: true }));
  };

  render() {
    if (!this.open) return html``;

    const environments = this.environmentData?.environments || [];
    const currentEnvironment = environments.find(env => env.is_current);
    const hasSelection = this.selectedEnvironmentId !== null;
    const selectedEnvironment = environments.find(env => env.id === this.selectedEnvironmentId);

    return html`
      <div class="environment-modal-overlay ${this.open ? 'open' : ''}" data-testid="environment-modal-overlay" @click=${this.closeModal}>
        <div class="environment-modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="environment-modal-header">
            <div class="environment-modal-title">
              🧰 Switch Inventory
            </div>
            <button class="environment-modal-close" data-testid="environment-modal-close-btn" @click=${this.closeModal}>✕</button>
          </div>

          <div class="environment-modal-body">
            <!-- Current Environment Info -->
            <div class="environment-info" data-testid="environment-current-info">
              <div class="current-environment">
                Currently at: ${currentEnvironment?.environment_name || 'Unknown Location'}
              </div>
              <div class="environment-subtitle">Choose a different inventory to switch to</div>
            </div>

            <!-- Environments Grid -->
            <div class="environments-grid">
              ${environments.map((environment: any) => html`
                <div
                  class="environment-card ${environment.is_current ? 'current' : ''} ${this.selectedEnvironmentId === environment.id ? 'selected' : ''}"
                  data-testid="environment-card-${environment.id}"
                  @click=${() => this.selectEnvironment(environment.id)}
                >
                  <div class="environment-card-header">
                    <div class="environment-icon">${this.getEnvironmentIcon(environment)}</div>
                    <div class="environment-name">${environment.environment_name || 'Unnamed Environment'}</div>
                    ${environment.is_current ? html`<div class="environment-status">CURRENT</div>` : ''}
                  </div>
                  <div class="environment-description">${environment.environment_description || 'No description available'}</div>
                  ${environment.generic_environment ? html`
                    <div class="environment-type">Type: ${environment.generic_environment.name}</div>
                  ` : ''}
                </div>
              `)}
            </div>
          </div>

          <div class="modal-actions">
            <button class="action-button cancel" data-testid="environment-cancel-btn" @click=${this.closeModal}>Cancel</button>
            <button
              class="action-button confirm"
              data-testid="environment-confirm-btn"
              @click=${this.confirmSwitch}
              ?disabled=${!hasSelection || this.selectedEnvironmentId === currentEnvironment?.id}
            >
              Switch to ${selectedEnvironment?.environment_name || 'Selected Environment'}
            </button>
          </div>
        </div>
      </div>
    `;
  }
}
